import { SiteInfo } from 'shared'
import { Settings } from '../settings-types'

interface MapboxFeature {
  center: [number, number]
  place_name?: string
  text?: string
  context?: Array<{ text: string }>
  [key: string]: unknown
}

const SEARCH_BOX_FORWARD =
  'https://api.mapbox.com/search/searchbox/v1/forward'

const SEARCH_BOX_CONTEXT_ORDER = [
  'country',
  'region',
  'postcode',
  'district',
  'place',
  'locality',
  'neighborhood',
  'street',
  'address',
] as const

function searchBoxFeatureToGeocodingLike(
  feature: Record<string, unknown>
): MapboxFeature {
  const geometry = feature.geometry as
    | { coordinates?: [number, number]; type?: string }
    | undefined
  const coords = geometry?.coordinates
  if (!coords || coords.length < 2) {
    throw new Error('Invalid Search Box feature: missing coordinates')
  }

  const props = (feature.properties ?? {}) as Record<string, unknown>
  const ctxObj = props.context as
    | Record<string, { name?: string }>
    | undefined
  const context: Array<{ text: string }> = []

  if (ctxObj) {
    for (const key of SEARCH_BOX_CONTEXT_ORDER) {
      const layer = ctxObj[key]
      if (layer?.name) {
        context.push({ text: layer.name })
      }
    }
  }

  const featureType = props.feature_type
  const placeType =
    featureType === 'poi'
      ? 'poi'
      : typeof featureType === 'string'
        ? featureType
        : 'place'

  const name = typeof props.name === 'string' ? props.name : ''
  const placeFormatted =
    typeof props.place_formatted === 'string' ? props.place_formatted : ''
  const fullAddress =
    typeof props.full_address === 'string' ? props.full_address : ''

  return {
    id: String(props.mapbox_id ?? ''),
    type: 'Feature',
    place_type: [placeType],
    text: name,
    place_name: fullAddress || placeFormatted || name,
    center: [coords[0], coords[1]],
    geometry: feature.geometry,
    context,
  }
}

function applyKoKrCenterOverrides(features: MapboxFeature[]): MapboxFeature[] {
  const originalCenters: Record<string, [number, number]> = {
    busan: [129.075236, 35.179953],
    seoul: [126.978291, 37.566679],
  }

  const locationOverrides: Record<string, { lat: number; lng: number }> = {
    busan: { lat: 35.160114406973875, lng: 129.0594986531328 },
    seoul: { lat: 37.493422561894505, lng: 127.02965814348164 },
  }

  return features.map((feature) => {
    for (const key in originalCenters) {
      const [origLng, origLat] = originalCenters[key]
      const [featLng, featLat] = feature.center
      if (origLng === featLng && origLat === featLat) {
        return {
          ...feature,
          center: [locationOverrides[key].lng, locationOverrides[key].lat],
        }
      }
    }
    return feature
  })
}

const mapbox = (settings: Settings, siteInfo: SiteInfo | null) => {
  const apiUrl = 'https://api.mapbox.com/geocoding/v5/mapbox.places'

  const defaultParams = {
    access_token: process.env.NEXT_PUBLIC_DOCTORS_MAPBOX_API_KEY,
    country: settings.country || siteInfo?.iso?.slice(-2).toLowerCase(),
    language: settings.language || siteInfo?.iso,
    types: [
      'region',
      'postcode',
      'district',
      'place',
      'locality',
      'neighborhood',
      'address',
    ],
  }

  const resolveStateAbbreviation = (value: string) => {
    const states = {
      al: 'Alabama',
      ak: 'Alaska',
      as: 'American Samoa',
      az: 'Arizona',
      ar: 'Arkansas',
      ca: 'California',
      co: 'Colorado',
      ct: 'Connecticut',
      de: 'Delaware',
      dc: 'District Of Columbia',
      fm: 'Federated States Of Micronesia',
      fl: 'Florida',
      ga: 'Georgia',
      gu: 'Guam',
      hi: 'Hawaii',
      id: 'Idaho',
      il: 'Illinois',
      in: 'Indiana',
      ia: 'Iowa',
      ks: 'Kansas',
      ky: 'Kentucky',
      la: 'Louisiana',
      me: 'Maine',
      mh: 'Marshall Islands',
      md: 'Maryland',
      ma: 'Massachusetts',
      mi: 'Michigan',
      mn: 'Minnesota',
      ms: 'Mississippi',
      mo: 'Missouri',
      mt: 'Montana',
      ne: 'Nebraska',
      nv: 'Nevada',
      nh: 'New Hampshire',
      nj: 'New Jersey',
      nm: 'New Mexico',
      ny: 'New York',
      nc: 'North Carolina',
      nd: 'North Dakota',
      mp: 'Northern Mariana Islands',
      oh: 'Ohio',
      ok: 'Oklahoma',
      or: 'Oregon',
      pw: 'Palau',
      pa: 'Pennsylvania',
      pr: 'Puerto Rico',
      ri: 'Rhode Island',
      sc: 'South Carolina',
      sd: 'South Dakota',
      tn: 'Tennessee',
      tx: 'Texas',
      ut: 'Utah',
      vt: 'Vermont',
      vi: 'Virgin Islands',
      va: 'Virginia',
      wa: 'Washington',
      wv: 'West Virginia',
      wi: 'Wisconsin',
      wy: 'Wyoming',
    }

    type StatesKey = keyof typeof states

    const regex = /(\b[a-zA-Z]{2})$/g
    const matches = value.toLowerCase().match(regex)

    if (matches && matches[1] !== undefined) {
      const fullStateName = Object.prototype.hasOwnProperty.call(
        states,
        matches[1]
      )
        ? states[matches[1] as StatesKey]
        : null

      if (fullStateName) {
        return value.replace(matches[1], fullStateName)
      }
    }

    return value
  }

  const geocode = async (text: string, customParams = {}) => {
    const url = new URL(`${apiUrl}/${text}.json`)
    const params = { ...defaultParams, ...customParams }

    type ParamsKey = keyof typeof params

    Object.keys(params).forEach((key) => {
      if (params[key as ParamsKey]) {
        let param: string | Array<string> = params[key as ParamsKey] ?? ''

        if (Array.isArray(params[key as ParamsKey])) {
          param = (params[key as ParamsKey] as Array<string>).join(',')
        }

        url.searchParams.append(key, param as string)
      }
    })

    return fetch(url.href).then((response) => {
      if (!response.ok) {
        throw new Error(response.statusText)
      }

      return response.json()
    })
  }

  const searchBoxForward = async (
    query: string,
    options?: { autocomplete?: boolean }
  ) => {
    const token = process.env.NEXT_PUBLIC_DOCTORS_MAPBOX_API_KEY
    if (!token) {
      throw new Error('NEXT_PUBLIC_DOCTORS_MAPBOX_API_KEY is not set')
    }

    const url = new URL(SEARCH_BOX_FORWARD)
    url.searchParams.set('q', query)
    url.searchParams.set('access_token', token)

    const countryCode =
      (Array.isArray(settings.country) && settings.country[0]) ||
      siteInfo?.iso?.slice(-2).toLowerCase() ||
      ''
    if (countryCode) {
      url.searchParams.set('country', countryCode)
    }

    const language =
      settings.mapboxSearchBoxLanguage ??
      siteInfo?.iso?.split('-')[0] ??
      'en'
    url.searchParams.set('language', language)

    const limit = Math.min(
      Math.max(1, settings.mapboxForwardGeocodeLimit ?? 5),
      10
    )
    url.searchParams.set('limit', String(limit))

    if (settings.mapboxSearchBoxTypes) {
      url.searchParams.set('types', settings.mapboxSearchBoxTypes)
    }

    if (
      typeof settings.map?.lng === 'number' &&
      typeof settings.map?.lat === 'number'
    ) {
      url.searchParams.set(
        'proximity',
        `${settings.map.lng},${settings.map.lat}`
      )
    }

    if (options?.autocomplete) {
      url.searchParams.set('auto_complete', 'true')
    }

    const response = await fetch(url.href)
    if (!response.ok) {
      throw new Error(
        `Search Box forward: ${response.status} ${response.statusText}`
      )
    }

    const data = (await response.json()) as {
      type?: string
      features?: Array<Record<string, unknown>>
    }

    if (!data.features?.length) {
      throw new Error('Search Box forward: no features')
    }

    const features = data.features.map(searchBoxFeatureToGeocodingLike)

    return {
      type: 'FeatureCollection' as const,
      query: query.split(/\s+/).filter(Boolean),
      features,
      attribution: '',
    }
  }

  const forwardGeocode = async (
    query: string,
    options?: { autocomplete?: boolean }
  ) => {
    if (settings.mapboxForwardProvider === 'searchbox') {
      return searchBoxForward(query, options)
    }

    const text = encodeURIComponent(
      siteInfo?.iso === 'en-US' ? resolveStateAbbreviation(query) : query
    )

    const geocodeParams: Record<string, unknown> = { limit: 5 }
    if ((options?.autocomplete ?? true) === false) {
      geocodeParams.autocomplete = false
    }

    const response = await geocode(text, geocodeParams)

    if (!response.features) return response

    if (siteInfo?.iso === 'ko-KR') {
      response.features = applyKoKrCenterOverrides(response.features)
    }

    return response
  }

  const reverseGeocode = (lat: number, lng: number) => {
    const text = `${lng},${lat}`

    return geocode(text, { limit: 1 })
  }

  return { forwardGeocode, reverseGeocode }
}

export default mapbox
